import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from '@thd-olt-component-react/grid';
import { DataProvider, useProduct } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { RouterContext, useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Buybox } from '@thd-olt-component-react/buybox';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { MediaGallery } from '@thd-olt-component-react/media-gallery';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { FeatureBasedRecommendations } from '@thd-olt-component-react/feature-based-recommendations';
import { RecommendationsPlaceholder, RecommendationsProvider } from '@thd-olt-component-react/recommendations';
import { QuestionsAndAnswers } from '@thd-olt-component-react/questions-and-answers';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { AugmentedReality } from '@thd-olt-component-react/augmented-reality';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { Price } from '@thd-olt-component-react/price';
import { DisclaimerMessage } from '@thd-olt-component-react/disclaimer-message';
import { PromotionProducts } from '@thd-olt-component-react/promotion-products';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { KeyFeatures, ProductOverview, QuickLinks } from '@thd-olt-component-react/product-overview';
import { Specifications } from '@thd-olt-component-react/specifications';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { Link, Links, StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { RatingsAndReviews } from '@thd-olt-component-react/ratings-and-reviews';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { ItemRelatedGroups } from '@thd-olt-component-react/item-related-groups';
import { SeoLinks } from '@thd-olt-component-react/seo-links';
import { MovingCalculator } from '@thd-olt-component-react/moving-calculator';
import { PIPRedirector } from '@thd-nucleus/redirector';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import '../styles/product-page.scss';

const batchedDomains = {
  search: [
    'appliedDimensions',
    'taxonomy',
    'dimensions',
    'metadata',
    'searchReport',
    'products',
    'templates'
  ],

  product: {
    server: [
      'identifiers',
      'info',
      'media',
      'details',
      'badges',
      'reviews',
      'availabilityType',
      'specificationGroup',
      'pricing',
      'taxonomy',
      'keyProductFeatures'
    ],

    client: [
      'identifiers',
      'info',
      'media',
      'details',
      'badges',
      'reviews',
      'availabilityType',
      'specificationGroup',
      'pricing',
      'taxonomy',
      'keyProductFeatures',
      'sizeAndFitDetail',
      'favoriteDetail',
      'installServices',
      'fulfillment'
    ]
  }
};

export const Product = (props) => {
  const ctx = useContext(ExperienceContext);
  const { channel, segment = '', isDebugMode } = ctx;
  const { itemId } = useParams();
  const storeId = useStoreId();
  const history = useHistory();
  const { pathname } = useLocation();
  const resx = {
    appId: 'homedepot01',
    maxProducts: 16,
    products: itemId,
    showATC: true,
    criteo: {
      version: 'model2'
    },
    event: 'product',
    carouselOptions: {
      fullScrollSkip: 2
    }
  };
  const isMobile = channel === 'mobile';
  const organicSearchSchema = isMobile ? 'pip_sem_mobile' : 'pipsem';
  // const [sskuItemId, setsskuItemId] = useState();
  // const domains = ['media'];
  // const resp = useProduct({
  //   itemId: sskuItemId,
  //   storeId,
  //   domains,
  //   batchedDomains: true,
  //   fetchPolicy: 'network-only',
  //   skip: !sskuItemId
  // });

  const updateStickyHeader = () => {
    const el = document.querySelector('.Header3');
    if (!el) return;
    if (channel === 'mobile') {
      el.classList.add('Header3-sticky');
    } else {
      el.classList.remove('Header3-sticky-desktop');
    }
  };

  useEffect(() => {
    updateStickyHeader();
  }, []);

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      history.push(canonicalUrl, { disableScroll: true });
    }
  };

  return (
    <DataProvider batchedDomains={batchedDomains} storeId={storeId} itemId={itemId}>
      <PIPRedirector disablePEPRedirect />
      <LayoutGroup name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </LayoutGroup>
      <RecommendationsProvider resx={resx}>
        <Row className="isBound">
          <LayoutManager
            data
            input={{
              channel,
              itemId,
              isDebugMode,
              segment
            }}
            name="pip-master"
          >
            {!isMobile
            && (
              <LayoutGroup tag={Row} name="sticknav">
                <StickyNav itemId={itemId}>
                  <Links>
                    <Link href="#product-overview">Product Overview</Link>
                    <Link href="#specifications">Specifications</Link>
                    <Link href="#questions_answers_full">Questions & Answers</Link>
                    <Link href="#ratings-and-reviews">Customer Reviews</Link>
                  </Links>
                </StickyNav>
              </LayoutGroup>
            )}
            <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="breadcrumb-sharebelt-grp">
              <LayoutManager name="breadcrumb-sharebelt">
                <LayoutGroup tag={Col} sm="10" flatten name="breadcrumbs">
                  <Breadcrumbs
                    itemId={itemId}
                    showInternet={isMobile}
                  />
                </LayoutGroup>
                <LayoutGroup tag={Col} sm="2" flatten name="sharebelt">
                  <Sharebelt horizontal itemId={itemId}>
                    <ShareBeltShareButton email />
                    <ShareBeltPrint />
                  </Sharebelt>
                </LayoutGroup>
              </LayoutManager>
            </LayoutGroup>
            {isMobile
              && (
                <LayoutGroup tag={Row} name="breadcrumb-sharebelt-grp">
                  <LayoutManager name="breadcrumb-event">
                    <LayoutGroup tag={Col} sm="12" flatten name="eventbreadcrumbs">
                      <Breadcrumbs
                        itemId={itemId}
                        shouldShowEventOnly
                      />
                    </LayoutGroup>
                  </LayoutManager>
                </LayoutGroup>
              )}
            <LayoutGroup tag={Row} id="recs:organic_search" name="recs:organic_search">
              <Col nopadding>
                <RecommendationsPlaceholder scheme={organicSearchSchema} hideRecommendation />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="zone-a">
              <Col nopadding={channel === 'desktop'}>
                <Row>
                  <Col>
                    <Discontinued itemId={itemId} />
                  </Col>
                  <Col sm="7" flatten className="product--sticky">
                    <div className="zone-card overflow u__clearfix">
                      <LayoutManager name="product-details-share">
                        {isMobile
                        && (
                          <LayoutGroup tag={Col} sm="12" name="product-details">
                            <ProductDetails itemId={itemId}>
                              <ProductDetails.Badge itemId={itemId} />
                              <div className="product-details__badge-title--wrapper">
                                <ProductDetails.Title />
                              </div>
                              <ProductDetails.BrandCollection showCollection />
                              <div className="product-details__favorites">
                                <Sharebelt itemId={itemId}>
                                  <ShareFavorite showCircle showFavoritesCount />
                                </Sharebelt>
                              </div>
                              <ProductDetails.Ratings
                                showBadge
                                target="#ratings-and-reviews"
                                offsetselector="#sticky-nav"
                                qaselector="#questions_answers_full"
                              />
                            </ProductDetails>
                          </LayoutGroup>
                        )}
                      </LayoutManager>
                      <LayoutGroup name="product-info-bar">
                        <Row>
                          <Col>
                            <ProductInfoBar itemId={itemId} />
                          </Col>
                        </Row>
                      </LayoutGroup>
                      <LayoutGroup tag={Col} sm="12">
                        <MediaGallery itemId={itemId} highRes showLoader />
                      </LayoutGroup>
                    </div>
                  </Col>
                  <Col sm="5" flatten>
                    <div className="zone-card overflow">
                      <Row>
                        <LayoutManager
                          name="buy-area"
                          input={{
                            channel,
                            segment
                          }}
                        >
                          <LayoutGroup tag={Row} name="product-details-share">
                            <LayoutManager name="product-details-share">
                              {channel !== 'mobile'
                              && (
                                <LayoutGroup tag={Col} sm="12" name="product-details">
                                  <ProductDetails itemId={itemId}>
                                    <ProductDetails.Badge itemId={itemId} />
                                    <div className="product-details__badge-title--wrapper">
                                      <ProductDetails.Title />
                                    </div>
                                    <ProductDetails.BrandCollection showCollection />
                                    <div className="product-details__favorites">
                                      <Sharebelt itemId={itemId}>
                                        <ShareFavorite showCircle showFavoritesCount />
                                      </Sharebelt>
                                    </div>
                                    <ProductDetails.Ratings
                                      showBadge
                                      target="#ratings-and-reviews"
                                      offsetselector="#sticky-nav"
                                      qaselector="#questions_answers_full"
                                    />
                                  </ProductDetails>
                                </LayoutGroup>
                              )}
                            </LayoutManager>
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="aisle-bay">
                            <AisleBay itemId={itemId} />
                          </LayoutGroup>
                          {channel !== 'mobile' && (
                            <LayoutGroup tag={Col} name="salient">
                              <SalientPoints itemId={itemId} max={3}>
                                <SalientListItem>
                                  {/* eslint-disable-next-line */}
                                  <a className="u__default-link" href="#product-overview">See More Details</a>
                                </SalientListItem>
                              </SalientPoints>
                            </LayoutGroup>
                          )}
                          <LayoutGroup tag={Col} name="order-sample">
                            <OrderSample itemId={itemId}>
                              Order a
                              <span className="title-case">
                                &nbsp;{segment === 'furniture' ? 'fabric' : segment}&nbsp;
                              </span>
                              Sample
                            </OrderSample>
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="price">
                            <Price
                              channel="desktop"
                              displayEachUom={false}
                              itemId={itemId}
                              large
                            />
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="disclaimer-message">
                            <DisclaimerMessage itemId={itemId} />
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="consumer-card-messaging">
                            <ConsumerCardMessaging itemId={itemId} />
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="gcc">
                            <GlobalCustomConfigurator itemId={itemId} />
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="super-sku">
                            <SuperSku itemId={itemId} onChange={onChange} />
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="moving-calculator">
                            <MovingCalculator itemId={itemId} />
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="promotion-products">
                            <PromotionProducts itemId={itemId} />
                          </LayoutGroup>
                          <LayoutGroup tag={Col} name="buybox">
                            <Buybox itemId={itemId} sticky />
                          </LayoutGroup>
                        </LayoutManager>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="augmented-reality">
              <div className="zone-card">
                <AugmentedReality itemId={itemId} instructions />
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="required-items">
              <Col nopadding>
                <RecommendationsPlaceholder scheme="required_items" />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="fbt">
              <div className="zone-card">
                <FrequentlyBoughtTogether itemId={itemId} />
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="product-overview" id="product-overview">
              <div className="zone-card">
                <ProductOverview itemId={itemId} channel={channel} />
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="IRG">
              <div className="zone-card">
                <Col>
                  <ItemRelatedGroups
                    itemId={itemId}
                    maxItems={6}
                    rowHeader="Get Everything You Need"
                    accordion
                    showMobileCardTabs
                  />
                </Col>
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="article-recommendations">
              <Col nopadding>
                <RecommendationsPlaceholder scheme="article_recs" defer />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="pip_sponsored">
              <Col nopadding>
                <RecommendationsPlaceholder scheme="pip_sponsored" />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="FBR">
              <div className="zone-card">
                <FeatureBasedRecommendations
                  hideOnMobile
                  itemId={itemId}
                />
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="key-features" id="key-features">
              <div className="zone-card">
                <KeyFeatures itemId={itemId} />
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="specifications">
              <div className="zone-card">
                <Specifications itemId={itemId} />
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="alternatives">
              <Col nopadding>
                <RecommendationsPlaceholder scheme="pip_alternatives" />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="visually-similar">
              <Col className="zone-card">
                <RecommendationsPlaceholder itemId={itemId} scheme="visuallysimilar_1_0" />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="questions" id="questions_answers_full" className="zone-card">
              <QuestionsAndAnswers itemId={itemId} />
            </LayoutGroup>
            <LayoutGroup tag={Row} name="ratings" id="ratings-and-reviews">
              <div className="zone-card">
                <RatingsAndReviews itemId={itemId} prosAndCons />
              </div>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="recently-viewed">
              <Col nopadding>
                <RecommendationsPlaceholder scheme="rv_gm_pip_rr" />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="related-search">
              <Col nopadding>
                <RelatedSearch itemId={itemId} />
              </Col>
            </LayoutGroup>
            <LayoutGroup tag={Row} name="seo-links">
              <SeoLinks path={pathname} />
            </LayoutGroup>
          </LayoutManager>
        </Row>
      </RecommendationsProvider>
    </DataProvider>
  );
};

Product.displayName = 'ProductPage';

Product.batchedDomains = [
  'details',
  'identifiers',
  'info',
  'media',
  'pricing',
  'reviews',
  'taxonomy',
  'specificationGroup'
];

Product.propTypes = {};
